import { Controller } from "@hotwired/stimulus";
import { TypedController } from "@vytant/stimulus-decorators";
import { z } from "@zod/mini";
import type { TurboFrame } from "~/types";

const REMOTE_MODAL_CONTROLLER_SCHEMA = z.object({
  frameId: z.string(),
  remoteUrl: z.string(),
  modalQueryParam: z.optional(z.string()),
  modalTitle: z.optional(z.string()),
  modalSizeHint: z.optional(z.string()),
});

@TypedController
export default class extends Controller<HTMLAnchorElement | HTMLButtonElement> {
  connect() {
    if (!REMOTE_MODAL_CONTROLLER_SCHEMA.safeParse(this.element.dataset).success) {
      console.error("Invalid dataset for modals controller", this.element.dataset);

      if (process.env.NODE_ENV === "development") {
        console.error(REMOTE_MODAL_CONTROLLER_SCHEMA.safeParse(this.element.dataset));
      }

      return;
    }

    this.element.addEventListener("click", (event) => {
      this.openDialog(event as MouseEvent);
    });
  }

  public static setData(element: HTMLAnchorElement | HTMLButtonElement, data: Partial<z.infer<typeof REMOTE_MODAL_CONTROLLER_SCHEMA>>, url?: string) {
    Object.entries(data).forEach(([key, value]) => {
      element.dataset[key] = value;
    });

    if (!element.dataset.controller) {
      element.dataset.controller = "admin--dashboard--remote-modal";
    }

    if (url && element instanceof HTMLAnchorElement) {
      element.href = url;
    }
  }

  private openDialog(event: MouseEvent) {
    // if middle clicking, ignore and let the default behavior happen
    if (event.button === 1) {
      return;
    }

    event.preventDefault();

    // Handle URL change
    if (this.config.modalQueryParam) {
      const newUrl = new URL(window.location.href);
      const modalQueryParam = this.config.modalQueryParam;

      newUrl.searchParams.set("modal", modalQueryParam!);
      window.history.pushState(window.history.state, "", newUrl.toString());
    }

    const modalTemplateElement = document.getElementById("modal-template")!;
    const modalTemplate = (modalTemplateElement.cloneNode(true) as HTMLTemplateElement).content;
    modalTemplate.querySelector(".ref-title")!.innerHTML = this.config.modalTitle || "Loading...";

    const turboFrame = document.createElement("turbo-frame") as TurboFrame;
    turboFrame.addEventListener("turbo:frame-missing", (event) => {
      event.preventDefault();

      const modalTemplateElement = document.getElementById("modal-template")!;
      const modalTemplate = (modalTemplateElement.cloneNode(true) as HTMLTemplateElement).content;
      modalTemplate.querySelector(".ref-title")!.innerHTML = this.config.modalTitle || "Loading...";
      modalTemplate.querySelector(".ref-content > .target")!.innerHTML = "";
      modalTemplate
        .querySelector(".ref-content > .target")!
        .appendChild(document.querySelector<HTMLTemplateElement>("#error-loading")!.content.cloneNode(true));

      turboFrame.classList.remove("hidden");
      turboFrame.innerHTML = "";
      turboFrame.appendChild(modalTemplate);
    });

    turboFrame.id = this.config.frameId;
    turboFrame.src = this.config.remoteUrl;
    turboFrame.appendChild(modalTemplate);

    if (this.config.modalSizeHint) {
      const modalSizes = this.modalSizes;
      if (modalSizes[this.config.modalSizeHint]) {
        Object.values(modalSizes)
          .flatMap((value) => value.split(" "))
          .forEach((className) => {
            this.dialog.classList.remove(className);
          });

        modalSizes[this.config.modalSizeHint].split(" ").forEach((className) => {
          this.dialog.classList.add(className);
        });
      }
    }

    this.dialog.innerHTML = "";
    this.dialog.appendChild(turboFrame);

    this.dialog.showModal();
  }

  private get dialog() {
    return document.querySelector<HTMLDialogElement>("#generic-modal")!;
  }

  private get config() {
    return REMOTE_MODAL_CONTROLLER_SCHEMA.parse(this.element.dataset);
  }

  private get modalSizes() {
    try {
      return z.record(z.string(), z.string()).parse(JSON.parse(document.getElementById("modal-sizes")!.textContent || "{}"));
    } catch {
      return {};
    }
  }
}
